import { css, cx } from '@linaria/core'
import { type FC, type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from '#components/Dropdown'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { SegmentedButton } from '#components/SegmentedButton'
import { Select } from '#components/Select'
import { LanguageSwitcher } from '#features/LanguageSwitcher'
import ChevronOneDownIcon from '#icons/chevron-1-down-48.svg'
import DesktopIcon from '#icons/device-desktop.svg'
import LargeScreenIcon from '#icons/device-largescreen.svg'
import NotebookIcon from '#icons/device-notebook.svg'
import AccessibilityIcon from '#icons/preferences-48.svg'
import {
  setHighlightPlaceholders,
  setSidepanelsBehavior,
  setTheme,
  useHighlightPlaceholderSelectItems,
  useSidepanelsSelectItems,
  useTranslatedThemeSelectItems,
} from '#store/componentConfiguration.ts'
import { setWidth, setZoom } from '#store/layoutConfiguration'
import { useSharedAppDispatch, useSharedAppSelector } from '#store/sharedBaseStore'
import { fragments, style } from '#styles'
import { flipIconClass } from '#styles/flipIcon'

interface AccessibilityPopupProps {
  className?: string
  children?: ReactNode
}

const dropdownClass = css`
  display: flex;
  align-items: center;
`

const popupClass = css`
  position: relative;
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0 0 16px 0;
  background-color: ${style.themeColors.backgroundPopout};
  color: ${style.themeColors.textDefault};
  outline: 0;

  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
`

const accessibilityDialogClass = css`
  width: 220px;
  display: flex;
  flex-direction: column;

  > ._line {
    content: ' ';
    width: 100%;
    height: 1px;
    border-top: 1px solid ${style.themeColors.lineDefault};
    margin-top: 16px;
  }

  > ._title {
    ${fragments.textStyle.body.s.regular}
    text-transform: uppercase;
    letter-spacing: 0.42px;
    color: ${style.themeColors.textDimmed};
    padding: 16px 16px 8px 16px;
  }

  > ._option {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 16px 4px 16px;


    > ._zoom-controls, > ._wideness-controls {
      width: 100%;

      > ._button > ._zoom-regular {
        ${fragments.textStyle.body.s.bold}
      }

      > ._zoom-large {
        ${fragments.textStyle.body.l.bold}
      }

      > ._zoom-extra-large {
        ${fragments.textStyle.body.l.bold}
        // font-size allowed here, it needs to be a bit smaller of a size that doesn't exist otherwise
        font-size: 20px;
      }
    }
  }
`

const AccessibilityOptions: FC<{ children?: ReactNode }> = ({ children }) => {
  const layoutConfiguration = useSharedAppSelector((x) => x.layoutConfiguration)
  const componentConfiguration = useSharedAppSelector((x) => x.componentConfiguration)
  const {
    zoom: { zoom },
    width: { width },
  } = layoutConfiguration
  const { theme, highlightPlaceholders, sidepanelBehavior } = componentConfiguration
  const dispatch = useSharedAppDispatch()
  const { t } = useTranslation('shared')

  const themeSelectItems = useTranslatedThemeSelectItems()
  const highlightPlaceholderSelectItems = useHighlightPlaceholderSelectItems()
  const sidepanelSelectItems = useSidepanelsSelectItems()

  return (
    <div className={accessibilityDialogClass}>
      <div className="_title">{t('lang.select', 'Select Language')}</div>
      <div className="_option">
        <LanguageSwitcher />
      </div>
      <div className="_title">{t('settings.view-options.header', 'View Options')}</div>
      <div className="_option">
        <SegmentedButton
          className="_zoom-controls"
          options={[
            {
              id: 'regular',
              content: <div className="_zoom-regular">Aa</div>,
              title: t('settings.view-options.zoom-regular', 'Default Zoom'),
            },
            {
              id: 'large',
              content: <div className="_zoom-large">Aa</div>,
              title: t('settings.view-options.zoom-large', 'Large Zoom'),
            },
            {
              id: 'extra-large',
              content: <div className="_zoom-extra-large">Aa</div>,
              title: t('settings.view-options.zoom-extra-large', 'Extra Large Zoom'),
            },
          ]}
          initialSelection={zoom}
          onSelectionChange={(value) => dispatch(setZoom(value))}
          kind="l"
          slim
        />

        <SegmentedButton
          className="_wideness-controls"
          options={[
            // { id: 'slim', content: <Icon iconUrl={TabletIcon} />, title: t('settings.view-options.slim', 'Slim') },
            { id: 'regular', content: <Icon iconUrl={NotebookIcon} />, title: t('settings.view-options.regular', 'Regular') },
            { id: 'wide', content: <Icon iconUrl={DesktopIcon} />, title: t('settings.view-options.wide', 'Wide') },
            { id: 'ultra-wide', content: <Icon iconUrl={LargeScreenIcon} />, title: t('settings.view-options.ultra-wide', 'Ultra Wide') },
          ]}
          initialSelection={width}
          onSelectionChange={(value) => {
            dispatch(setWidth(value))
          }}
          kind="l"
          slim
        />
      </div>
      <div className="_option">
        <Select
          label={t('settings.contrast-theme.header', 'Contrast Theme')}
          placeholder={t('settings.contrast-theme.empty-placeholder', 'Select Theme')}
          value={theme}
          options={themeSelectItems}
          onChange={(v) => dispatch(setTheme(v ?? 'default'))}
          slim
        />
      </div>

      {children && (
        <>
          <div className="_line" />
          {children}
        </>
      )}

      <div className="_line" />
      <div className="_title">{t('settings.dev.header', 'Dev Options')}</div>
      <div className="_option">
        <Select
          label={t('settings.dev.highlight-placeholders.header', 'Highlight Placeholders')}
          placeholder={t('settings.dev.highlight-placeholders.empty-placeholder', 'Select placeholder highlighting')}
          value={highlightPlaceholders}
          options={highlightPlaceholderSelectItems}
          onChange={(v) => dispatch(setHighlightPlaceholders(v ?? 'no'))}
          slim
        />

        <Select
          label={t('settings.dev.sidepanels.header', 'Sidepanel')}
          placeholder={t('settings.dev.sidepanels.placeholder', 'Select Sidepanel behavior')}
          value={sidepanelBehavior}
          options={sidepanelSelectItems}
          onChange={(v) => dispatch(setSidepanelsBehavior(v ?? 'disable'))}
          slim
        />
      </div>
    </div>
  )
}

export const AccessibilityDropdown: FC<AccessibilityPopupProps> = ({ className, children }) => {
  const { t } = useTranslation('shared')
  const [isOpen, setIsOpen] = useState(false)
  return (
    <Dropdown
      popupClassName={popupClass}
      open={isOpen}
      onOpenChange={setIsOpen}
      dropdownContent={<AccessibilityOptions>{children}</AccessibilityOptions>}
      className={dropdownClass}
      offset={7.5}
      arrowOffset={3}
    >
      <IconButton
        iconSize="m"
        className={cx(className, 'icon-left')}
        style={{ opacity: 1 }}
        iconUrl={AccessibilityIcon}
        title={t('settings.button.show-settings', 'Show User Settings')}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
      >
        <Icon iconUrl={ChevronOneDownIcon} size="xxs" className={cx(flipIconClass, isOpen && 'flipped')} />
      </IconButton>
    </Dropdown>
  )
}
